/**
 * `idempotencyKey` isn't part of `GenerateCreativesRequest` — it's owned and
 * minted by `useGenerateCreatives` (smithers, not mad-hooks — it hand-rolls
 * its own state rather than going through this SDK via a mad-hooks wrapper),
 * not the caller, so it's passed to {@link GenerateJobs.generate} as its own
 * argument instead.
 *
 * `username` is likewise left off: the server derives the requester from the
 * authenticated request, not the body, so the proto field is deprecated and
 * ignored. Not exposing it here keeps no consumer depending on a dead field.
 */
export const generateCreativesRequestToService = (request) => ({
    brand_kit_id: request.brandKitId,
    brand_data: request.brandData,
    goal: request.goal,
    sizes: request.sizes,
    styles: request.styles,
    prompt: request.prompt,
    advertiser_url: request.advertiserUrl
});
export const generateJobResponseFromService = (response) => ({
    // Every field is technically optional on the generated type (OpenAPI
    // codegen doesn't carry proto3's required/optional distinction through),
    // but the service always sends all three on this response — same
    // conservatism/fallback pattern as googlePlaceFromService above.
    jobId: response.job_id ?? "",
    brandSlug: response.brand_slug ?? "",
    status: response.status ?? ""
});
export const qualityDefectsFromService = (defects) => ({
    hasClipping: defects.has_clipping,
    hasGarbledText: defects.has_garbled_text,
    hasFakeLogo: defects.has_fake_logo,
    hasFakeFont: defects.has_fake_font,
    hasLowContrastCta: defects.has_low_contrast_cta,
    hasBrandColorMismatch: defects.has_brand_color_mismatch,
    hasCtaMisalignment: defects.has_cta_misalignment,
    hasIndustryMismatch: defects.has_industry_mismatch,
    hasHeadlineImageMismatch: defects.has_headline_image_mismatch,
    hasCtaImageMismatch: defects.has_cta_image_mismatch,
    hasGenericCta: defects.has_generic_cta,
    hasVisualDistortion: defects.has_visual_distortion,
    hasBlur: defects.has_blur,
    ...("issues" in defects && { issues: defects.issues })
});
export const qualityScoreFromService = (score) => ({
    brandFidelity: score.brand_fidelity,
    professionalCraft: score.professional_craft,
    campaignPotential: score.campaign_potential,
    emotionalImpact: score.emotional_impact,
    originality: score.originality,
    weightedTotal: score.weighted_total,
    ...(score.defects && { defects: qualityDefectsFromService(score.defects) })
});
export const generatedCreativeAssetFromService = (asset) => ({
    // url/size/width/height are always present on the wire for a real asset,
    // same conservatism/fallback pattern as googlePlaceFromService above.
    url: asset.url ?? "",
    size: asset.size ?? "",
    width: asset.width ?? 0,
    height: asset.height ?? 0,
    ...("critic_rounds" in asset && { criticRounds: asset.critic_rounds }),
    ...(asset.quality_score && { qualityScore: qualityScoreFromService(asset.quality_score) }),
    ...("image_prompt" in asset && { imagePrompt: asset.image_prompt }),
    ...("raw_url" in asset && { rawUrl: asset.raw_url }),
    ...("size_bytes" in asset && { sizeBytes: asset.size_bytes }),
    ...("gcs_key" in asset && { gcsKey: asset.gcs_key }),
    ...("raw_gcs_key" in asset && { rawGcsKey: asset.raw_gcs_key })
});
export const creativeVariantFromService = (variant) => ({
    // goal/style_variant/headlines are always present on the wire for a real
    // variant, same conservatism/fallback pattern as googlePlaceFromService.
    goal: variant.goal ?? "",
    styleVariant: variant.style_variant ?? "",
    ...("style_name" in variant && { styleName: variant.style_name }),
    headlines: variant.headlines ?? [],
    ...("tagline" in variant && { tagline: variant.tagline }),
    ...("cta_text" in variant && { ctaText: variant.cta_text }),
    assets: (variant.assets ?? []).map(generatedCreativeAssetFromService),
    ...("selection_reason" in variant && { selectionReason: variant.selection_reason })
});
export const generateJobStatusResponseFromService = (response) => ({
    // job_id/status are always present on the wire, same conservatism/fallback
    // pattern as googlePlaceFromService above.
    jobId: response.job_id ?? "",
    ...("brand_slug" in response && { brandSlug: response.brand_slug }),
    ...("brand_name" in response && { brandName: response.brand_name }),
    ...("brand_kit_id" in response && { brandKitId: response.brand_kit_id }),
    ...("advertiser_url" in response && { advertiserUrl: response.advertiser_url }),
    ...("username" in response && { username: response.username }),
    ...("goal" in response && { goal: response.goal }),
    ...("sizes" in response && { sizes: response.sizes }),
    ...(response.created_at && { createdAt: new Date(response.created_at) }),
    status: response.status ?? "",
    ...(response.variants && { variants: response.variants.map(creativeVariantFromService) }),
    ...("error" in response && { error: response.error }),
    ...("disabled" in response && { disabled: response.disabled })
});
//# sourceMappingURL=generateJobs.js.map